import { CloseOutlined } from "@ant-design/icons"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Drawer, Form, DatePicker } from "antd"
import { Button } from "components"
import ImageComponent from "components/custom/image"
import { CustomInput } from "components/new-stuff/form"
import dayjs from "dayjs"
import { useAppNotificationStore } from "stores"
import { useCycleDurationID } from "stores/cycle-duration"
import { newArchAxiosInstance } from "utils/request"
import { onQueryError } from "utils/utils"

interface props {
  handleClose: () => void
  id?: number | null
  keyResultId?: number | null
  pageType: "admin" | "manager" | "employee"
  keyResultName: string
  handleSuccess: () => void
}

const CreateInitiative = ({
  handleClose,
  id,
  pageType,
  keyResultId,
  keyResultName,
  handleSuccess,
}: props) => {
  const { toast } = useAppNotificationStore()
  const [form] = Form.useForm()
  const queryClient = useQueryClient()
  const cdId = useCycleDurationID({
    type: pageType,
  })

  const { isLoading: isSubmitting, mutate: submit } = useMutation({
    mutationFn: async (body: any) => {
      const endpoint =
        pageType === "admin" || pageType === "manager"
          ? id
            ? `/api/v2/admin/okr/employee/initiative/${id}`
            : `/api/v2/admin/okr/employee/initiative`
          : `/api/v2/employee/okr/initiative`
      const method = id ? "patch" : "post"

      const res = await newArchAxiosInstance.request({
        url: endpoint,
        method,
        data: body,
      })
      return res.data
    },
    onSuccess: (data) => {
      if (data.success) {
        data.message && toast.success(data.message)
        handleClose()
        handleSuccess()
        queryClient.invalidateQueries({
          queryKey: [`/api/v2/employee/okr/departmentobjectives/${cdId}`],
        })
      }
    },
    onError: onQueryError,
  })
  const onFinish = (values: any) => {
    const payload = {
      initiativeName: values.initiativeName,
      timeLine: values.timeLine
        ? dayjs(values.timeLine).format("YYYY-MM-DD")
        : null,
      keyResultId: keyResultId,
    }
    submit(payload)
  }
  return (
    <Drawer
      width={675}
      title={
        <span className=" text-padeBlack font-circular text-xl">
          Add Initiative{" "}
        </span>
      }
      open={true}
      onClose={handleClose}
      closeIcon={false}
      className="bg-white relative"
      extra={
        <button onClick={handleClose}>
          {" "}
          <CloseOutlined className="h-6 w-6" />
        </button>
      }
      footer={
        <div>
          <Button
            color="primary"
            className=" w-full"
            onClick={() => form.submit()}
            submitting={isSubmitting}
          >
            Create
          </Button>
        </div>
      }
    >
      <div className="">
        <div className="">
          {keyResultName && (
            <div className=" mb-9 flex items-center gap-4">
              <div className=" w-4 h-4 flex justify-center items-center ">
                <ImageComponent
                  className="object-cover h-full w-full"
                  src={
                    "https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/3ff8c694-cdf9-467e-a291-021d2ff075f7.svg"
                  }
                  alt=""
                />
              </div>
              <h5 className=" text-padeBlack font-bold text-[15px] ">
                {keyResultName}{" "}
              </h5>
            </div>
          )}
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
          >
            <div>
              <CustomInput
                label="Initiative Name "
                name="initiativeName"
                required
                placeholder="E.g Increase Revenue"
              />

              <Form.Item
                name="timeLine"
                className="w-full flex gap-2 flex-col"
                label={
                  <span className="text-padeBlack font-circular text-sm">
                    Timeline (Optional)
                  </span>
                }
              >
                <DatePicker
                  className="w-full"
                  placeholder="Date"
                  disabledDate={(date) => {
                    const endDate = dayjs()

                    const afterEndDate = date.isBefore(endDate)

                    return afterEndDate
                  }}
                />
              </Form.Item>
            </div>
          </Form>
        </div>
      </div>
    </Drawer>
  )
}

export default CreateInitiative
